/**
 * Tag Service for fetching company letter tags
 * Connects to the main ContraVault API for tag management
 */

export interface TagWithCount {
  tag: string;
  letter_count: number;
  created_at?: string;
  score?: number;
}

export interface FetchTagsResponse {
  message: string;
  company_name: string;
  tags: TagWithCount[];
  total_count: number;
  next_cursor?: string | null;
  has_more?: boolean;
}

export interface SearchTagsResponse {
  message: string;
  company_name: string;
  query: string;
  tags: TagWithCount[];
  total_count: number;
}

class TagService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = window.location.origin;
    console.log(`[TAG_SERVICE] Initialized with baseUrl: ${this.baseUrl}`);
  }

  /**
   * Fetch company letter tags (paginated)
   * @param companyName - Company/tenant name
   * @param contractId - Base collection name for the tender
   * @param limit - Number of tags to fetch
   * @param cursor - Pagination cursor
   */
  async fetchTags(
    companyName: string,
    contractId: string,
    limit: number = 50,
    cursor: string | null = null
  ): Promise<FetchTagsResponse> {
    console.log(`[TAG_SERVICE] Fetching tags for company: ${companyName}, contract: ${contractId}`);

    try {
      const url = `${this.baseUrl}/api/outlook/tags`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'fetch',
          companyName,
          contractId,
          limit,
          cursor
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`[TAG_SERVICE] Fetched ${result.tags?.length || 0} tags`);
      return result;
    } catch (error) {
      console.error(`[TAG_SERVICE] Error fetching tags:`, error);
      throw error;
    }
  }

  /**
   * Search company letter tags with fuzzy matching
   * @param companyName - Company/tenant name
   * @param contractId - Base collection name for the tender
   * @param query - Search query
   * @param limit - Number of results to return
   */
  async searchTags(
    companyName: string,
    contractId: string,
    query: string,
    limit: number = 10
  ): Promise<SearchTagsResponse> {
    console.log(`[TAG_SERVICE] Searching tags: "${query}" for company: ${companyName}`);

    try {
      const url = `${this.baseUrl}/api/outlook/tags`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'search',
          companyName,
          contractId,
          query,
          limit
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`[TAG_SERVICE] Found ${result.tags?.length || 0} matching tags`);
      return result;
    } catch (error) {
      console.error(`[TAG_SERVICE] Error searching tags:`, error);
      throw error;
    }
  }

  /**
   * Insert a new company letter tag
   * @param companyName - Company/tenant name
   * @param contractId - Base collection name for the tender
   * @param tag - New tag name to insert
   */
  async insertTag(
    companyName: string,
    contractId: string,
    tag: string
  ): Promise<any> {
    console.log(`[TAG_SERVICE] Inserting new tag: "${tag}" for company: ${companyName}`);

    try {
      const url = `${this.baseUrl}/api/outlook/tags`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          action: 'insert',
          companyName,
          contractId,
          tag
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      console.log(`[TAG_SERVICE] Tag inserted successfully`);
      return result;
    } catch (error) {
      console.error(`[TAG_SERVICE] Error inserting tag:`, error);
      throw error;
    }
  }

  /**
   * Parse comma-separated tag string into array
   */
  parseTags(tagString: string): string[] {
    return tagString
      .split(',')
      .map(tag => tag.trim())
      .filter(tag => tag.length > 0);
  }

  /**
   * Format tag array into comma-separated string
   */
  formatTags(tags: string[]): string {
    return tags.join(', ');
  }
}

export const tagService = new TagService();
export default tagService;
